import {
	ButtonBase,
	LinearProgress,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { ChevronRight } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import { useGoalStatusStyle } from "web_myplann/Goals/Hooks/useGoalStatusStyle";
import {
	GOAL_CATEGORY_CONFIG,
	type Goal,
	STATUS_LABEL_MAP,
} from "web_myplann/Goals/Types/GoalTypes";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { IconBox } from "web_myplann/Shared/Components/IconBox";

type Props = {
	goal: Goal;
};

export const GoalListItem: FC<Props> = ({ goal }) => {
	const theme = useTheme();
	const { t } = useResource();

	const formatAmount = useFormatGoalAmount();

	const styleConfig = useGoalStatusStyle(goal.status);
	const statusLabelResource = goal.status && STATUS_LABEL_MAP[goal.status];
	const statusLabel = statusLabelResource ? t(statusLabelResource) : "";
	const categoryConfig = GOAL_CATEGORY_CONFIG[goal.category];
	const CategoryIcon = categoryConfig.icon;
	const progress =
		goal.totalValue > 0
			? Math.min(100, Math.round((goal.coveredAmount / goal.totalValue) * 100))
			: 0;

	const navigate = useNavigate();

	const handleGoalClick = (goalId: string) => {
		navigate(Routing.Goals.GoalDetail.path(goalId));
	};

	return (
		<ButtonBase
			onClick={() => handleGoalClick(goal.id)}
			sx={{ width: "100%", textAlign: "left" }}
		>
			<Stack
				direction="row"
				alignItems="center"
				gap={1.2}
				px={1.6}
				py={1.2}
				width="100%"
			>
				<IconBox bg={categoryConfig.bgColor}>
					<CategoryIcon size={20} color={categoryConfig.color} />
				</IconBox>

				<Stack flex={1} minWidth={0}>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						gap={1}
						mb={0.4}
					>
						<Typography
							variant="bodyStrong"
							color={theme.palette.grey[900]}
							sx={{
								flex: 1,
								minWidth: 0,
								display: "-webkit-box",
								WebkitLineClamp: 2,
								WebkitBoxOrient: "vertical",
								overflow: "hidden",
								textOverflow: "ellipsis",
								wordBreak: "break-word",
							}}
						>
							{goal.name}
						</Typography>
						{statusLabel && (
							<Typography
								variant="button"
								color={styleConfig.color}
								sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
							>
								{statusLabel}
							</Typography>
						)}
					</Stack>

					<LinearProgress
						variant="determinate"
						slim
						value={progress}
						sx={{
							mb: 0.4,
							"& .MuiLinearProgress-bar": {
								bgcolor: styleConfig.progressColor,
							},
						}}
					/>

					<Typography variant="caption" color={theme.palette.grey[500]} tabular>
						{formatAmount(goal.coveredAmount)} / {formatAmount(goal.totalValue)}{" "}
						{t(Resources.Goals.Amount.currencySign)} · {goal.dueIn}
					</Typography>
				</Stack>

				<ChevronRight size={16} color={theme.palette.grey[400]} />
			</Stack>
		</ButtonBase>
	);
};
